import React, { useMemo, useEffect, useState } from 'react';
import SubTableV2 from "./SubTableV2";

function TransactionDetailsModal({ isOpen, details, onClose }) {
    const [formattedDetails, setFormattedDetails] = useState(null);

    useEffect(() => {
        if (details) {
            const lineItemsData = details.lineItems.map(item => ({
                ...item,
                RetailPPU: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3
                }).format(item.RetailPPU),
                UserPPU: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3
                }).format(item.UserPPU),
                UserAmount: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(item.UserAmount),
                UserTransactionFee: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(item.UserTransactionFee),
                Discount: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(item.UserDiscount) //(item.RetailPPU * item.Quantity - item.UserAmount) + item.UserTransactionFee
            }));

            const taxesData = details.transLineTaxes.map(tax => ({
                ...tax,
                amount: new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(tax.amount)
            }));

            setFormattedDetails({ ...details, lineItems: lineItemsData, transLineTaxes: taxesData });
        }
    }, [details]);

    const lineItemsColumns = useMemo(() => [
        {
            name: 'Category',
            selector: row => row.Category,
            sortable: true,
            width: '200px'
        },
        {
            name: 'Retail Price',
            selector: row => row.RetailPPU,
            sortable: true,
        },
        {
            name: 'Paid Price',
            selector: row => row.UserPPU,
            sortable: true,
        },
        {
            name: 'Quantity',
            selector: row => row.Quantity,
            sortable: true,
        },
        {
            name: 'Trans Fee',
            selector: row => row.UserTransactionFee,
            sortable: true,
        },
        {
            name: 'Discount',
            selector: row => row.Discount,
            sortable: true,
        },
        {
            name: 'Amount',
            selector: row => row.UserAmount,
            sortable: true,
        }
    ], []);

    const transLineTaxesColumns = useMemo(() => [
        {
            name: 'Tax Description',
            selector: row => row.taxDescription,
            sortable: true,
        },
        {
            name: 'Amount',
            selector: row => row.amount, // Assuming already formatted as currency
            sortable: true,
        }
    ], []);

    const transactionInfosColumns = useMemo(() => [
        {
            name: 'Type',
            selector: row => row.Type,
            sortable: true,
        },
        {
            name: 'Value',
            selector: row => row.Value,
            sortable: true,
        }
    ], []);



    if (!isOpen || !formattedDetails) { 
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50">
            {/* Modal Container */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-lg sm:rounded-t-xl">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Transaction Details</h2>
                            <p className="text-xs sm:text-sm text-gray-600 truncate">ID: {details.transactions.TransactionId}</p>
                        </div>
                    </div>
                    <button
                        className="btn-base btn-ghost btn-xs sm:btn-sm flex-shrink-0"
                        onClick={onClose}
                    >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                    {/* Transaction Summary */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 border border-blue-200">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Transaction Summary</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">POS Date</span>
                                <span className="text-sm text-gray-900">{new Date(details.transactions.POSDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">Transaction Date</span>
                                <span className="text-sm text-gray-900">{new Date(details.transactions.TransactionDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">Authorization Code</span>
                                <span className="text-sm text-gray-900">{details.transactions.AuthCode}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">Card Number</span>
                                <span className="text-sm text-gray-900">{details.transactions.CardNumber}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">Hand Entered</span>
                                <span className="text-sm text-gray-900">{details.transactions.HandEntered ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">Location</span>
                                <span className="text-sm text-gray-900">{details.transactions.locationName}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">Location ID</span>
                                <span className="text-sm text-gray-900">{details.transactions.LocationId}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">Chain ID</span>
                                <span className="text-sm text-gray-900">{details.transactions.chainId}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-600">State</span>
                                <span className="text-sm text-gray-900">{details.transactions.locationState}</span>
                            </div>
                        </div>
                    </div>

                    {/* Tables */}
                    <div className="space-y-6">
                        <SubTableV2
                            title="Line Items"
                            columns={lineItemsColumns}
                            data={formattedDetails.lineItems}
                        />
                        <SubTableV2
                            title="Taxes Paid (Included in the amount)"
                            columns={transLineTaxesColumns}
                            data={formattedDetails.transLineTaxes}
                        />
                        <SubTableV2
                            title="Prompts / Information"
                            columns={transactionInfosColumns}
                            data={details.transactionInfos}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg sm:rounded-b-xl">
                    <button
                        className="btn-base btn-secondary btn-sm sm:btn-base"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TransactionDetailsModal;
